import React from 'react';
import { Box, Button, Divider, Group, Text } from '@mantine/core';
import { IconChevronRight, IconRocket } from '@tabler/icons-react';
import { StepProps } from '../types';
import { useOnboardingStyles } from '../onboardingStyles';
import { useOnbColors } from '../onboardingColors';
import { StepVideo } from '../common';
import pageStyles from '../Onboarding.module.css';

const NEXT_STEPS = [
  {
    emoji: '⭐',
    title: 'Create Your First Campaign',
    badge: 'Recommended',
    desc: 'Start here. Create a campaign so Visiopt can organize your traffic, tests, and reporting.',
    to: 'manage-campaigns',
  },
  {
    emoji: '🧪',
    title: 'Create Your First Test',
    desc: 'Launch your first optimization test and begin improving your website.',
    to: 'create-test',
  },
  {
    emoji: '📊',
    title: 'Customize Your Dashboard',
    desc: 'Personalize your dashboard to display the reports and metrics that matter most to your business.',
    to: 'dashboard',
  },
  {
    emoji: '📚',
    title: 'Help Center',
    desc: 'Explore tutorials, guides, and onboarding resources whenever you need assistance.',
    href: 'https://visiopt.com/help-center.html',
  },
  {
    emoji: '💬',
    title: 'Contact Support',
    desc: 'Have a question? Our team is here to help.',
    href: 'mailto:support@visiopt.com',
  },
];

const HELP_LINKS = [
  { emoji: '📧', label: 'Email Support', href: 'mailto:support@visiopt.com' },
  { emoji: '📚', label: 'Help Center', href: 'https://visiopt.com/help-center.html' },
  { emoji: '🎥', label: 'Video Tutorials', href: 'https://visiopt.com/help-videos.html' },
];

export function Complete({ goToDashboard }: StepProps) {
  const { classes } = useOnboardingStyles();
  const col = useOnbColors();

  // Every internal destination finishes onboarding first, so completion is always persisted
  const handleDestination = (to: string) => goToDashboard?.(to);

  return (
    <Box className={`${classes.inner} ${pageStyles.fadeIn}`} style={{ maxWidth: 720 }}>
      <h1 className={classes.title} style={{ textAlign: 'center' }}>
        🎉 You're Ready to Optimize!
      </h1>
      <p className={classes.subtitle} style={{ fontWeight: 400, color: col.textSecondary, textAlign: 'center' }}>
        Congratulations! Your Visiopt account has been successfully configured and you're ready to
        start optimizing your website. From here you can begin creating campaigns, launching tests,
        and increasing conversions.
      </p>

      {/* Personal welcome video */}
      <Box style={{ width: '100%', marginTop: 22 }}>
        <StepVideo
          variant="hero"
          badge="Welcome"
          title="A personal welcome from Scott"
          sublabel="Watch this quick intro to get the most out of Visiopt"
          modalTitle="Welcome to Visiopt"
          videoUrl="https://vimeo.com/1135809081"
        />
        <Text ta="center" fz={13} c="dimmed" mt={8}>
          A short welcome and a tour of where to go next.
        </Text>
      </Box>

      {/* Recommended next steps */}
      <div className={classes.subheading} style={{ marginTop: 28 }}>
        Recommended Next Steps
      </div>
      <Box style={{ width: '100%', marginTop: 16, display: 'grid', gap: 10 }}>
        {NEXT_STEPS.map((s, i) => {
          const recommended = Boolean(s.badge);
          const content = (
            <>
              <span
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 10,
                  background: recommended ? col.purpleBg : col.surface,
                  display: 'grid',
                  placeItems: 'center',
                  fontSize: 19,
                  flexShrink: 0,
                }}
              >
                {s.emoji}
              </span>
              <Box style={{ flex: 1, minWidth: 0 }}>
                <Group spacing={8} noWrap>
                  <Text fw={700} fz="sm" c={col.textPrimary}>
                    {i + 1}. {s.title}
                  </Text>
                  {recommended && (
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: col.purple,
                        background: col.purpleBg,
                        borderRadius: 999,
                        padding: '2px 8px',
                        letterSpacing: '0.02em',
                      }}
                    >
                      {s.badge}
                    </span>
                  )}
                </Group>
                <Text fz={13} c={col.textGray} mt={2}>
                  {s.desc}
                </Text>
              </Box>
              <IconChevronRight size={18} color={col.textMuted} style={{ flexShrink: 0 }} />
            </>
          );
          const rowStyle: React.CSSProperties = {
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            width: '100%',
            textAlign: 'left',
            padding: '14px 16px',
            borderRadius: 12,
            border: `1px solid ${recommended ? col.purpleBorder : col.border}`,
            background: recommended ? col.purpleBg : col.card,
            cursor: 'pointer',
            textDecoration: 'none',
          };
          return s.href ? (
            <a key={s.title} href={s.href} target="_blank" rel="noreferrer" style={rowStyle}>
              {content}
            </a>
          ) : (
            <button key={s.title} type="button" style={rowStyle} onClick={() => handleDestination(s.to as string)}>
              {content}
            </button>
          );
        })}
      </Box>

      <Divider my={26} w="100%" />

      {/* Need help */}
      <div className={classes.subheading}>Need Help?</div>
      <Text ta="center" fz={13.5} c="dimmed" mt={4}>
        We're here whenever you need us.
      </Text>
      <Group position="center" spacing={10} mt={14}>
        {HELP_LINKS.map((h) => (
          <a key={h.label} className={classes.helpPill} href={h.href} target="_blank" rel="noreferrer">
            <span>{h.emoji}</span> {h.label}
          </a>
        ))}
      </Group>

      <Button className={classes.blackBtn} mt={28} px={26} leftIcon={<IconRocket size={18} />} onClick={() => goToDashboard?.()}>
        Go to Dashboard
      </Button>
    </Box>
  );
}
